import { z } from 'zod/v3';
import type { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
  RADAR_ID_PATTERN,
  TECH_RADAR_SCHEMA_VERSION,
} from './constants';
import { isSafeLinkUrl } from './links';

const titleSchema = z.string().min(1).max(MAX_TITLE_LENGTH);
const requiredDescriptionSchema = z.string().min(1).max(MAX_DESCRIPTION_LENGTH);
const optionalDescriptionSchema = z
  .string()
  .max(MAX_DESCRIPTION_LENGTH)
  .optional();
const radarIdSchema = z.string().regex(RADAR_ID_PATTERN);

function isParseableIsoDate(value: string): boolean {
  return !Number.isNaN(Date.parse(value));
}

function hasDuplicates(values: string[]): boolean {
  return new Set(values).size !== values.length;
}

// ---- Source documents (authored YAML in appfolio/virtual-architecture-team) ----

export const ringDefinitionSchema = z.object({
  id: radarIdSchema,
  name: z.string().min(1),
  position: z.number().int(),
  color: z.string().min(1),
  description: optionalDescriptionSchema,
});

export const quadrantDefinitionSchema = z.object({
  id: radarIdSchema,
  name: z.string().min(1),
});

export const ringChangeSchema = z.object({
  ring: z.string().min(1),
  date: z
    .string()
    .refine(isParseableIsoDate, 'date must be a parseable ISO-8601 string'),
  description: optionalDescriptionSchema,
});

export const entryLinkSchema = z.object({
  title: titleSchema,
  url: z.string().refine(isSafeLinkUrl, 'url must use http or https'),
});

export const radarEntrySourceSchema = z.object({
  title: titleSchema,
  description: requiredDescriptionSchema,
  quadrant: z.string().min(1),
  history: z.array(ringChangeSchema).min(1),
  links: z.array(entryLinkSchema).optional(),
});

export const radarDefinitionSchema = z
  .object({
    title: titleSchema,
    description: requiredDescriptionSchema,
    default: z.boolean().optional(),
    rings: z.array(ringDefinitionSchema).min(1),
    quadrants: z.array(quadrantDefinitionSchema).length(4),
  })
  .superRefine((radar, ctx) => {
    if (hasDuplicates(radar.rings.map(ring => ring.id))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rings'],
        message: 'ring ids must be unique within a radar',
      });
    }
    if (hasDuplicates(radar.rings.map(ring => String(ring.position)))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rings'],
        message: 'ring positions must be unique within a radar',
      });
    }
    if (hasDuplicates(radar.quadrants.map(quadrant => quadrant.id))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['quadrants'],
        message: 'quadrant ids must be unique within a radar',
      });
    }
  });

// ---- Published documents (JSON in the radar storage bucket) ----

export const radarIndexEntrySchema = z.object({
  id: radarIdSchema,
  title: titleSchema,
  description: requiredDescriptionSchema,
  entryCount: z.number().int().nonnegative(),
});

export const radarIndexSchema = z
  .object({
    schemaVersion: z.literal(TECH_RADAR_SCHEMA_VERSION),
    defaultRadarId: z.string(),
    radars: z.array(radarIndexEntrySchema).min(1),
  })
  .superRefine((index, ctx) => {
    if (!index.radars.some(radar => radar.id === index.defaultRadarId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultRadarId'],
        message: 'defaultRadarId must appear in radars',
      });
    }
  });

const radarQuadrantSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const radarRingSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  description: z.string().optional(),
});

const radarEntrySnapshotSchema = z.object({
  date: z
    .string()
    .refine(isParseableIsoDate, 'date must be a parseable ISO-8601 string'),
  ringId: z.string(),
  description: z.string().optional(),
  moved: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
});

const radarEntryLinkSchema = z.object({
  title: z.string(),
  url: z.string(),
});

const radarEntrySchema = z.object({
  id: z.string(),
  key: z.string(),
  title: z.string(),
  description: z.string(),
  quadrant: z.string(),
  timeline: z.array(radarEntrySnapshotSchema).min(1),
  links: z.array(radarEntryLinkSchema).optional(),
});

export const radarDocumentSchema = z.object({
  schemaVersion: z.literal(TECH_RADAR_SCHEMA_VERSION),
  id: radarIdSchema,
  title: titleSchema,
  description: requiredDescriptionSchema,
  quadrants: z.array(radarQuadrantSchema).length(4),
  rings: z.array(radarRingSchema).min(1),
  entries: z.array(radarEntrySchema),
});

/**
 * Converts a validated RadarDocument into the upstream TechRadarLoaderResponse
 * shape, turning each snapshot's wire-format ISO string into a real Date —
 * the one conversion upstream's TechRadarComponent expects internally. The
 * return type annotation makes this a compile-time check: if RadarDocument
 * ever drops a field TechRadarLoaderResponse needs, this fails to compile.
 * Callers pass the result straight to techRadarApiRef consumers with no
 * further adapter.
 */
export function toTechRadarLoaderResponse(
  document: z.infer<typeof radarDocumentSchema>,
): TechRadarLoaderResponse {
  return {
    quadrants: document.quadrants,
    rings: document.rings,
    entries: document.entries.map(entry => ({
      ...entry,
      timeline: entry.timeline.map(snapshot => ({
        ...snapshot,
        date: new Date(snapshot.date),
      })),
    })),
  };
}
